/**
 * Solo and Coach: the engine runs here, and the computer is shared/ai.ts.
 * Randomness lives in this file (seeded, replayable), never in the engine.
 */

import { type Card, fullDeck, randomSeed, rng, shuffle } from '@shared/cards';
import {
  type GameEvent,
  type GameState,
  type Seat,
  type SeatActionBody,
  IllegalAction,
  mustGo,
  newGame,
  other,
  readyToDeal,
  step,
} from '@shared/engine';
import { chooseDiscard, choosePeg, type Position } from '@shared/ai';
import { viewFor, type View } from '@shared/view';
import { type ConnStatus, Emitter, type GameAdapter } from './adapter';

const LOCAL_ERROR_TEXT: Partial<Record<string, string>> = {
  MUST_PLAY: 'You have a card that fits — you must play it.',
  OVER_31: 'That card takes the count past 31.',
  NOT_YOUR_TURN: 'Not your turn.',
  ALREADY_DONE: 'Already done.',
  NOT_IN_HAND: 'That card is not in your hand.',
  STALE_SEQ: 'That tap arrived late.',
};

/** Pacing (UX spec): the computer never answers faster than a person could see. */
const BOT_DELAY = { discard: 700, cut: 500, play: 900, go: 700, ack: 0 } as const;

export class LocalGame implements GameAdapter {
  readonly mode: 'solo' | 'coach';
  readonly you: Seat = 0;
  readonly bot: Seat = 1;
  state: GameState;
  seed: number;
  private random: () => number;
  private views = new Emitter<[View, GameEvent[]]>();
  private status = new Emitter<[ConnStatus]>();
  private errors = new Emitter<[string, string]>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private botDealt: Card[] = [];
  private closed = false;
  /** Replay log for bug reports and tests. */
  log: { seed: number; actions: unknown[] } = { seed: 0, actions: [] };

  constructor(mode: 'solo' | 'coach', seed = randomSeed()) {
    this.mode = mode;
    this.seed = seed;
    this.random = rng(seed);
    this.log.seed = seed;
    this.state = newGame();
    this.start();
  }

  private start() {
    const events: GameEvent[] = [];
    let s = newGame();
    for (let i = 0; i < 50 && s.phase === 'cut-for-deal'; i++) {
      const r = step(s, { t: 'cut-for-deal', deck: this.deck() });
      s = r.state;
      events.push(...r.events);
    }
    this.state = s;
    this.noteDeal();
    queueMicrotask(() => {
      this.emit(events);
      this.scheduleBot();
    });
  }

  private deck(): Card[] {
    return shuffle(fullDeck(), this.random);
  }

  private noteDeal() {
    if (this.state.phase === 'discard') this.botDealt = [...this.state.hands[this.bot]];
  }

  private emit(events: GameEvent[]) {
    if (this.closed) return;
    this.views.emit(viewFor(this.state, this.you), events);
  }

  current(): View {
    return viewFor(this.state, this.you);
  }

  /** The six cards YOU were dealt this hand (known locally). */
  yourDealt(): Card[] {
    return this.dealtYou;
  }
  private dealtYou: Card[] = [];

  private apply(action: Parameters<typeof step>[1]): GameEvent[] {
    const r = step(this.state, action);
    this.state = r.state;
    this.log.actions.push(action);
    if (action.t === 'deal' || action.t === 'cut-for-deal') {
      this.noteDeal();
      this.dealtYou = [...this.state.hands[this.you]];
    }
    return r.events;
  }

  dispatch(body: SeatActionBody) {
    if (this.closed) return;
    try {
      const events = this.apply({ ...body, seat: this.you, seq: this.state.seq });
      events.push(...this.followUps());
      this.emit(events);
      this.scheduleBot();
    } catch (e) {
      if (e instanceof IllegalAction) {
        this.errors.emit(e.code, LOCAL_ERROR_TEXT[e.code] ?? 'That play is not allowed.');
        this.emit([]);
        return;
      }
      throw e;
    }
  }

  /** Authority duties: deal when both acked the hand end. */
  private followUps(): GameEvent[] {
    const out: GameEvent[] = [];
    if (readyToDeal(this.state)) {
      out.push(...this.apply({ t: 'deal', deck: this.deck() }));
    }
    return out;
  }

  private botPosition(): Position {
    const s = this.state;
    return { myScore: s.scores[this.bot], oppScore: s.scores[this.you], isDealer: s.dealer === this.bot, handNo: s.handNo };
  }

  private botSeen(): Card[] {
    const s = this.state;
    const seen = new Set<Card>(this.botDealt);
    if (s.starter !== null) seen.add(s.starter);
    if (s.peg) for (const side of s.peg.played) for (const c of side) seen.add(c);
    return [...seen];
  }

  /** Decide what the computer should do now, and when. */
  private scheduleBot() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.closed) return;
    const s = this.state;
    const b = this.bot;
    let body: SeatActionBody | null = null;
    let delay = 0;
    switch (s.phase) {
      case 'discard':
        if (!s.submitted[b]) {
          const choice = chooseDiscard(s.hands[b], s.dealer === b, { myScore: s.scores[b], oppScore: s.scores[this.you], handNo: s.handNo });
          body = { t: 'discard', cards: [choice.throw[0]!, choice.throw[1]!] };
          delay = BOT_DELAY.discard;
        }
        break;
      case 'cut-starter':
        if (other(s.dealer!) === b) {
          body = { t: 'cut' };
          delay = BOT_DELAY.cut;
        }
        break;
      case 'pegging':
        if (s.peg!.turn === b) {
          if (mustGo(s, b)) {
            body = { t: 'go' };
            delay = BOT_DELAY.go;
          } else {
            const pick = choosePeg({ hand: s.hands[b], stack: s.peg!.stack, seen: this.botSeen(), oppCards: s.hands[this.you].length, pos: this.botPosition() });
            if (pick) {
              body = { t: 'play', card: pick.card };
              delay = BOT_DELAY.play;
            }
          }
        }
        break;
      case 'show':
      case 'hand-end':
        if (!s.acked[b]) {
          body = { t: 'ack' };
          delay = BOT_DELAY.ack;
        }
        break;
      default:
        break;
    }
    if (!body) return;
    const act = body;
    const run = () => {
      this.timer = null;
      if (this.closed) return;
      try {
        const events = this.apply({ ...act, seat: b, seq: this.state.seq });
        events.push(...this.followUps());
        this.emit(events);
      } catch (e) {
        if (!(e instanceof IllegalAction)) throw e;
      }
      this.scheduleBot();
    };
    if (delay === 0) run();
    else this.timer = setTimeout(run, delay);
  }

  onView(cb: (view: View, events: GameEvent[]) => void) {
    return this.views.on(cb);
  }
  onStatus(cb: (s: ConnStatus) => void) {
    queueMicrotask(() => cb({ k: 'local' }));
    return this.status.on(cb);
  }
  onRoom() {
    return () => {};
  }
  onError(cb: (code: string, message: string) => void) {
    return this.errors.on(cb);
  }
  rematch() {
    this.newGame();
  }
  endAbandoned() {}
  leave() {}
  newGame() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.seed = randomSeed();
    this.random = rng(this.seed);
    this.log = { seed: this.seed, actions: [] };
    this.start();
  }
  close() {
    this.closed = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }
}
